import { Controller } from '@hotwired/stimulus'
import PriceInputController from '@/public/behaviors/PriceInput'
import BidConfirmModalController from '@/public/blocks/BidConfirmModal/controller'
import InputController from '@/public/components/Input/controller'
import { formatPrice } from '@/utils/functions'
import { AlertType } from '@/public/components/Alert/types'

const DEFAULT_ERROR_MESSAGE = 'Ошибка при создании ставки, повторите попытку позже'

/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static outlets = ['price-input']
    static targets = [
        'imageWrapper',
        'image',
        'link',
        'badgeLeading',
        'badgeOutbid',
        'bidForm',
        'status',
        'currentPrice',
    ]
    static values = {
        lotNumber: Number,
        type: String,
        isLeading: Boolean,
        currentPrice: Number,
        userLastBid: Number,
    }

    declare readonly imageWrapperTarget: HTMLElement
    declare readonly hasImageWrapperTarget: boolean
    declare readonly imageTargets: HTMLElement[]
    declare readonly hasImageTarget: boolean
    declare readonly linkTarget: HTMLElement
    declare readonly hasLinkTarget: boolean

    declare priceInputOutlet: PriceInputController
    declare hasPriceInputOutlet: boolean

    declare badgeLeadingTarget: HTMLElement
    declare hasBadgeLeadingTarget: boolean
    declare badgeOutbidTarget: HTMLElement
    declare hasBadgeOutbidTarget: boolean
    declare bidFormTarget: HTMLElement
    declare hasBidFormTarget: boolean
    declare statusTarget: HTMLElement
    declare hasStatusTarget: boolean
    declare currentPriceTarget: HTMLElement
    declare hasCurrentPriceTarget: boolean

    declare lotNumberValue: number
    declare typeValue: string
    declare isLeadingValue: boolean
    declare currentPriceValue: number
    declare userLastBidValue: number

    private isLeading: boolean = false
    private currentPrice: number = 0
    private userLastBid: number | null = null
    private sendingRequest: boolean = false
    private inputController: InputController | null = null

    private get isBiddable(): boolean {
        return this.typeValue === 'open' || this.typeValue === 'express'
    }

    connect() {
        if (this.hasImageWrapperTarget) {
            this.imageWrapperTarget.addEventListener('mousemove', this.handleMouseMove)
        }

        if (this.hasImageTarget) {
            this.imageTargets.forEach((img) => img.addEventListener('click', this.handleImageClick))
        }

        this.isLeading = this.isLeadingValue || false
        this.currentPrice = this.currentPriceValue || 0
        this.userLastBid = this.userLastBidValue ?? null

        this.upsertBidUi()

        queueMicrotask(() => {
            const inputController = this.resolveInputController()

            if (!inputController) {
                return
            }

            inputController.element.addEventListener('input:error-added', this.handleInputErrorAdded.bind(this))
            inputController.element.addEventListener('input:error-cleared', this.handleInputErrorCleared.bind(this))
        })
    }

    disconnect() {
        if (this.hasImageWrapperTarget) {
            this.imageWrapperTarget.removeEventListener('mousemove', this.handleMouseMove)
        }

        if (this.hasImageTarget) {
            this.imageTargets.forEach((img) => img.removeEventListener('click', this.handleImageClick))
        }
    }

    handleImageClick = () => {
        if (!this.hasLinkTarget) {
            return
        }

        this.linkTarget.click()
    }

    handleMouseMove = (event: MouseEvent) => {
        if (!this.hasImageTarget) {
            return
        }

        const containerRect = this.imageWrapperTarget.getBoundingClientRect()
        const cursorX = event.clientX - containerRect.left
        const containerWidth = containerRect.width
        const imagesCount = this.imageTargets.length

        if (imagesCount === 1) {
            return
        }

        const segmentWidth = containerWidth / imagesCount
        const targetIndex = Math.floor(cursorX / segmentWidth)

        const currentImg = this.imageTargets.find((img) => !img.classList.contains('hidden'))
        const currentIndex = currentImg ? parseInt(currentImg.dataset.index || '0', 10) : 0

        if (currentIndex === targetIndex) {
            return
        }

        this.imageTargets.forEach((img) => img.classList.add('hidden'))
        const targetImg = this.imageTargets.find((img) => parseInt(img.dataset.index || '0', 10) === targetIndex)

        if (targetImg) {
            setTimeout(() => targetImg.classList.remove('hidden'), 10)
        }
    }

    updateBidStatus(isLeading: boolean, currentPrice: number, userLastBid?: number) {
        if (!this.isBiddable) {
            return
        }

        this.isLeading = isLeading
        this.currentPrice = currentPrice

        if (userLastBid !== undefined) {
            this.userLastBid = userLastBid
        }

        if (this.hasPriceInputOutlet) {
            this.priceInputOutlet.setCurrentPrice(this.currentPrice)
        }

        this.upsertBidUi()
    }

    async handleSubmit(event: Event) {
        event.preventDefault()
        event.stopPropagation()

        if (!this.isBiddable || this.sendingRequest || !this.hasPriceInputOutlet) {
            return this.showAlert(DEFAULT_ERROR_MESSAGE, 'error', 5000)
        }

        const price = this.priceInputOutlet.getNumericValue()

        if (!price || price <= this.currentPrice) {
            return this.setInputError(`Ставка должна быть больше текущей ставки: ${formatPrice(this.currentPrice)}`)
        }

        this.clearInputError()

        try {
            const modalElement = document.getElementById('bid-confirm-modal')

            if (!modalElement) {
                return this.setInputError(DEFAULT_ERROR_MESSAGE)
            }

            const controller = this.application.getControllerForElementAndIdentifier(
                modalElement,
                'bid-confirm-modal'
            ) as BidConfirmModalController | null

            if (!controller) {
                return this.setInputError(DEFAULT_ERROR_MESSAGE)
            }

            await controller.open(this.lotNumberValue, price)
        } catch (error) {
            this.setInputError(DEFAULT_ERROR_MESSAGE)
        }
    }

    handleInputErrorAdded() {
        this.bidFormTarget.classList.add('has-error')
    }

    handleInputErrorCleared() {
        this.bidFormTarget.classList.remove('has-error')
    }

    private displayLeading() {
        if (this.hasBadgeLeadingTarget) {
            this.badgeLeadingTarget.classList.remove('hidden')
        }

        if (this.hasBadgeOutbidTarget) {
            this.badgeOutbidTarget.classList.add('hidden')
        }

        if (this.hasBidFormTarget) {
            this.bidFormTarget.classList.add('hidden')
        }

        if (this.hasStatusTarget) {
            this.statusTarget.classList.remove('hidden')
        }
    }

    private displayOutbid() {
        if (this.hasBadgeLeadingTarget) {
            this.badgeLeadingTarget.classList.add('hidden')
        }

        if (this.hasBadgeOutbidTarget) {
            this.badgeOutbidTarget.classList.remove('hidden')
        }

        if (this.hasBidFormTarget) {
            this.bidFormTarget.classList.remove('hidden')
        }

        if (this.hasStatusTarget) {
            this.statusTarget.classList.add('hidden')
        }
    }

    private hideBidForm() {
        if (this.hasBadgeLeadingTarget) {
            this.badgeLeadingTarget.classList.add('hidden')
        }

        if (this.hasBadgeOutbidTarget) {
            this.badgeOutbidTarget.classList.add('hidden')
        }

        if (this.hasBidFormTarget) {
            this.bidFormTarget.classList.add('hidden')
        }

        if (this.hasStatusTarget) {
            this.statusTarget.classList.remove('hidden')
        }
    }

    private upsertBidUi() {
        if (!this.isBiddable) {
            return this.hideBidForm()
        }

        if (this.hasCurrentPriceTarget && this.currentPrice > 0) {
            this.currentPriceTarget.textContent = formatPrice(this.currentPrice)
        }

        const isParticipating = this.userLastBid !== null && this.userLastBid > 0

        if (!isParticipating) {
            return this.hideBidForm()
        }

        if (this.isLeading) {
            this.displayLeading()
        } else {
            this.displayOutbid()
        }
    }

    private showAlert(message: string, type: AlertType, timeout?: number) {
        window.dispatchEvent(new CustomEvent('alert:show', { detail: { message, type, timeout }, bubbles: true }))
    }

    private setInputError(message: string) {
        const inputController = this.resolveInputController()

        if (!inputController) {
            return
        }

        inputController.setError(message)
    }

    private clearInputError()
    {
        const inputController = this.resolveInputController()

        if (!inputController) {
            return
        }

        inputController.clearError()
    }

    private resolveInputController(): InputController|null
    {
        if (!this.inputController && this.hasPriceInputOutlet) {
            this.inputController = this.application.getControllerForElementAndIdentifier(
                this.priceInputOutlet.element,
                'input'
            ) as InputController | null
        }

        return this.inputController
    }
}
